import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, Sparkles, Loader2 } from 'lucide-react';
import { useProductMatrix, TABS } from '../contexts/ProductMatrixContext';
import { mapToParentPlatform } from '../utils/platformMapping';
import { Product } from '../pages/ProductMatrixNew';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeToggle } from './ThemeToggle';
import { Shift4Logo } from './Shift4Logo';

interface HeaderProps {
  isAIOpen: boolean;
  aiPanelWidth?: number;
}

const Header: React.FC<HeaderProps> = ({ isAIOpen, aiPanelWidth = 600 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toggleAIDrawer,
    activeTab,
    setActiveTab,
    isDisclaimerDismissed,
    setSelectedRegion,
    setSelectedCountry,
    setSelectedPlatform,
    selectedPlatform,
    setBrowseMode,
    currentView
  } = useProductMatrix();

  // Determine which header tab should be highlighted based on current view and platform
  const getActiveHeaderTab = (): string => {
    // If we're on a platform detail page, determine tab based on platform category
    if (currentView === 'platform' && selectedPlatform) {
      const platformName = selectedPlatform.toLowerCase();
      if (platformName.includes('3pi') ||
          platformName.includes('partner integrations') ||
          platformName.includes('integrated payments') ||
          platformName.includes('shift4 mobile')) {
        return '3PI';
      } else if (platformName.includes('payments') && !platformName.includes('integrated')) {
        return 'Shift4 Payments';
      } else {
        return 'Products';
      }
    }
    // Otherwise use the activeTab (for home page navigation)
    return activeTab;
  };

  const activeHeaderTab = getActiveHeaderTab();

  // Local search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    product: Product;
    platform: string;
    tab: string;
  }>>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Calculate dropdown position for fixed positioning
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (searchInputRef.current && showSearchResults) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition);
    };
  }, [showSearchResults]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        const dropdown = document.getElementById('search-dropdown');
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setShowSearchResults(false);
        }
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchResults]);

  // Search functionality
  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();

      if (data.success) {
        const results: Array<{ product: Product; platform: string; tab: string }> = data.data
          .map((product: Product) => {
            const mappedPlatform = mapToParentPlatform(product.platform);
            return {
              product,
              platform: mappedPlatform.platform,
              tab: mappedPlatform.tab
            };
          })
          // Filter out Integrated Payments products that aren't "Ready"
          .filter((result: { product: Product; platform: string; tab: string }) => {
            if (result.platform === 'Integrated Payments' && result.product.development !== 'Ready') {
              return false;
            }
            return true;
          });

        setSearchResults(results);

        // Track search in Datadog
        if (datadogRum) {
          datadogRum.addAction(`search: "${query}"`, {
            query: query,
            resultCount: results.length,
            hasResults: results.length > 0,
            location: 'header'
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);

      // Track failed search in Datadog
      if (datadogRum) {
        datadogRum.addAction(`search error: "${query}"`, {
          query: query,
          error: error instanceof Error ? error.message : 'Unknown error',
          location: 'header'
        });
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchResultClick = (result: { product: Product; platform: string; tab: string }) => {
    navigate(`/platform/${encodeURIComponent(result.platform)}?tab=${result.tab}&highlight=${encodeURIComponent(result.product.solution)}&from=header-search`);
    setShowSearchResults(false);
    setSearchQuery('');

    // Track search result click in Datadog
    if (datadogRum) {
      datadogRum.addAction('search_result_click', {
        solution: result.product.solution,
        platform: result.platform,
        tab: result.tab,
        location: 'header'
      });
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Clear location filters to show all platforms
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedPlatform(null);
    // Enable browse mode to show all platform cards
    setBrowseMode(true);

    // Determine target section
    const sectionId = tabId === 'Products' ? 'products-section'
      : tabId === '3PI' ? 'integrations-section'
      : 'payments-section';

    // Determine hash for URL
    const hash = tabId === 'Products' ? ''
      : tabId === '3PI' ? '#integrations'
      : '#payments';

    // Navigate to home with hash
    navigate(`/${hash}`);

    // Dispatch scroll event for HomePageV4 to handle
    setTimeout(() => {
      const scrollEvent = new CustomEvent('scrollToSection', {
        detail: { sectionId }
      });
      window.dispatchEvent(scrollEvent);
    }, 100);
  };

  const handleLogoClick = () => {
    // Clear location filters to show all platforms
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedPlatform(null);
    // Enable browse mode to show all platform cards
    setBrowseMode(true);
    // Set Products tab as active to show cards immediately
    setActiveTab('Products');

    // Navigate to home (no hash = top/Products section)
    navigate('/');

    // Dispatch scroll event to scroll to Products section (top)
    setTimeout(() => {
      const scrollEvent = new CustomEvent('scrollToSection', {
        detail: { sectionId: 'products-section' }
      });
      window.dispatchEvent(scrollEvent);
    }, 100);
  };

  return (
    <>
      {/* Fixed Two-Row Header */}
      <header
        className={`fixed inset-x-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/80 transition-all duration-300 ease-in-out ${
          isDisclaimerDismissed ? 'top-0' : 'top-[56px]'
        }`}
        style={{
          right: isAIOpen ? `${aiPanelWidth}px` : '0',
        }}
      >
        {/* Row 1: Logo, Search, AI Button - 3 Column Grid */}
        <div className="flex h-16 items-center justify-between px-6 lg:grid lg:grid-cols-3 lg:px-8">
          {/* LEFT: Logo + Product Matrix Text (Desktop only) */}
          <div className="hidden lg:flex lg:items-center lg:gap-2">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity"
              aria-label="Home"
            >
              <Shift4Logo className="h-6 w-auto" />
              <span className="text-xl font-semibold text-blue-400 dark:text-blue-300 tracking-[0.1em]">
                MATRIX
              </span>
            </button>
          </div>

          {/* MOBILE: Logo only (shown on small screens) */}
          <button
            onClick={handleLogoClick}
            className="lg:hidden flex items-center hover:opacity-80 transition-opacity"
            aria-label="Home"
          >
            <Shift4Logo className="h-5 w-auto" />
          </button>

            {/* CENTER: Search + AI Button (Desktop only) */}
            <div className="hidden lg:flex lg:items-center lg:justify-center lg:gap-3">
              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <div className="relative h-8 flex items-center">
                  <Search className="pointer-events-none absolute left-2 h-5 w-5 stroke-slate-500" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Find products..."
                    className="h-8 w-full appearance-none rounded-full border border-slate-400 bg-white/50 pl-9 pr-10 text-sm text-slate-900 placeholder:text-slate-500 transition focus:border-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
                    aria-label="Search products"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                        setShowSearchResults(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
              </div>

              {/* Ask AI Button */}
              <button
                onClick={toggleAIDrawer}
                className="h-8 flex items-center gap-2 rounded-full bg-blue-500 px-3 text-sm font-medium text-white transition hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                aria-label="Open AI Assistant"
              >
                <Sparkles className="h-4 w-4" />
                <span>Ask AI</span>
              </button>
            </div>

          {/* RIGHT: Theme Toggle */}
          <div className="hidden lg:flex lg:items-center lg:justify-end">
            <ThemeToggle />
          </div>

          {/* Mobile: Search Icon + Theme Toggle + AI Button */}
          <div className="flex lg:hidden items-center gap-2 ml-auto">
            {/* Mobile Search Icon */}
            <button
              onClick={() => {
                // TODO: Implement mobile search modal
                console.log('Mobile search not implemented yet');
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Mobile Theme Toggle */}
            <ThemeToggle />

            {/* Mobile AI Button */}
            <button
              onClick={toggleAIDrawer}
              className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 font-medium transition-colors shadow-sm"
              aria-label="Open AI Assistant"
            >
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Row 2: Tab Navigation (Desktop only) */}
        <div className="hidden px-6 lg:block lg:px-8">
          <nav className="flex items-center gap-6" aria-label="Product categories">
            {TABS.map((tab) => {
              const isActive = activeHeaderTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`
                    py-3 text-sm/5 transition border-b-2
                    ${isActive
                      ? 'font-medium border-blue-600 text-gray-900 dark:text-gray-100'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }
                  `}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Search Results Dropdown */}
      {showSearchResults && searchResults.length > 0 && (
        <div
          id="search-dropdown"
          className="fixed rounded-lg shadow-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto z-[60]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className="px-3 py-2 text-xs font-medium border-b text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
          </div>
          {searchResults.map((result) => (
            <button
              key={`${result.platform}-${result.product.id}`}
              onClick={() => handleSearchResultClick(result)}
              className="w-full px-3 py-3 text-left border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-100 dark:border-gray-700 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {result.product.solution}
              </div>
              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                {result.platform} • {result.product.category}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showSearchResults && searchQuery && searchResults.length === 0 && !isSearching && (
        <div
          id="search-dropdown"
          className="fixed rounded-lg shadow-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 px-4 py-8 text-center z-[60] text-gray-500 dark:text-gray-400"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          No products found for "{searchQuery}"
        </div>
      )}
    </>
  );
};

export default Header;
